import { Pressable, Text, TextInput, View } from "react-native";
import { useTranslation } from "react-i18next";

import { Icon } from "../Icon/Icon";
import { ConfirmRemoveButton } from "../primitives/ConfirmRemoveButton";
import type { EditorStyles } from "../ProgramEditor/styles";
import type { UiTheme } from "../theme";
import { ComposerSetTable } from "./ComposerSetTable";
import type { ComposerStyles } from "./composerStyles";
import type { PlanStyles } from "./planComposerStyles";
import type { Composer } from "./useComposer";
import { stateAria } from "../a11y";

// The expanded, edit-in-place editor for ONE plan exercise (solo or
// superset). It is the SAME reused ComposerSetTable the program composer
// uses — only the chrome around it changes: a header (name + ▾ collapse)
// and a two-step ✕ remove (armed, then confirmed; never colour-only). No
// per-exercise Save/Cancel: edits are live (logger model), persisted to
// the in-memory plan the moment you collapse / switch / Save. Removing the
// last member of an exercise removes the exercise itself (onRemove).
export function PlanExerciseEditor({
  composer,
  resolveName,
  cs,
  ps,
  selectStyles,
  theme,
  onCollapse,
  onRemove,
  onReplaceMember,
}: {
  composer: Composer;
  resolveName: (exerciseId: string, snapshot?: string) => string;
  cs: ComposerStyles;
  ps: PlanStyles;
  selectStyles: EditorStyles;
  theme: UiTheme;
  onCollapse: () => void;
  onRemove: () => void;
  onReplaceMember: (memberIdx: number) => void;
}) {
  const { t } = useTranslation();
  const {
    form,
    updateRow,
    setMemberKind,
    setMemberInstructions,
    addRound,
    removeRound,
    removeMember,
    cellUnit,
  } = composer;

  const names = form.members.map((m) => resolveName(m.exerciseId, m.exerciseName));
  const title = form.grouped ? names.join(" + ") : names[0] ?? "";

  // ✕ on a member head: removing the last remaining member means the whole
  // exercise leaves the plan — route that to onRemove so a solo never
  // collapses into an empty editor.
  const handleRemoveMember = (memberIdx: number) => {
    if (form.members.length <= 1) onRemove();
    else removeMember(memberIdx);
  };

  return (
    <View style={ps.editorFrame}>
      <View style={ps.editorHead}>
        <Text style={ps.editorTitle} numberOfLines={1}>
          {title}
        </Text>
        <Pressable
          accessibilityRole="button"
          accessibilityState={{ expanded: true }}
          {...stateAria({ expanded: true })}
          accessibilityLabel={t("plans.collapseExercise", {
            defaultValue: "Collapse exercise",
          })}
          onPress={onCollapse}
          hitSlop={8}
          style={ps.collapseBtn}
        >
          <Icon name="chevron-down" size={ps.collapseText.fontSize} color={theme.palette.ink3} />
        </Pressable>
      </View>

      <ComposerSetTable
        members={form.members}
        grouped={form.grouped}
        onRemoveMember={handleRemoveMember}
        onReplaceMember={onReplaceMember}
        onUpdateRow={updateRow}
        onSetKind={setMemberKind}
        onSetInstructions={setMemberInstructions}
        onAddRound={addRound}
        onRemoveRound={removeRound}
        cellUnit={cellUnit}
        styles={cs}
        selectStyles={selectStyles}
        theme={theme}
      />

      {/* Per-exercise note — discoverable (the set table's 📝 toggle was a
          bare dot nobody found). Solo only: a superset edits each member's
          note via the in-table 📝. Bound to slot instructions = the note the
          logger surfaces under the exercise. */}
      {!form.grouped ? (
        <View style={{ gap: 4 }}>
          <Text style={cs.fieldLabel}>
            {t("plans.exerciseNoteLabel", { defaultValue: "Exercise note (optional)" })}
          </Text>
          <TextInput
            style={[cs.input, cs.inputMultiline]}
            value={form.members[0]?.instructions ?? ""}
            onChangeText={(v) => setMemberInstructions(0, v)}
            placeholder={t("composer.exerciseNotePlaceholder", {
              defaultValue: "Note for this exercise (shown while logging)",
            })}
            placeholderTextColor={theme.palette.ink3}
            multiline
            maxLength={500}
            accessibilityLabel={t("composer.exerciseNote", { defaultValue: "Exercise note" })}
          />
        </View>
      ) : null}

      <ConfirmRemoveButton
        onRemove={onRemove}
        style={ps.removeBtn}
        textStyle={ps.removeBtnText}
        confirmLabel={t("plans.removeExerciseConfirm", { defaultValue: "Remove this exercise?" })}
        removeLabel={t("plans.removeExercise", { defaultValue: "Remove exercise" })}
        confirmHint={t("plans.removeExerciseHint", { defaultValue: "Tap once to confirm, then tap again to remove." })}
      />
    </View>
  );
}
